import {Component, computed, effect, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {TranslocoPipe} from '@jsverse/transloco';
import {BackgroundCardComponent} from '../../../shared/components/titled-background-card/background-card.component';
import {ConfirmStepLeaveDirective} from '../../../shared/project-step-save-button/confirm-step-leave.directive';
import {Button} from 'primeng/button';
import {
  ProviderContractRequest,
  ProviderContractResponse,
  ProviderContractStatus,
  AdminProviderContractControllerService,
  UpsertProviderContractRequestParams,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {firstValueFrom, forkJoin} from 'rxjs';
import {map} from 'rxjs/operators';
import {DirectusService, HrTool} from '@frontend/directus';
import {ProviderContractRow} from './selecthor-admin-provider-list.types';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {SelectComponent} from '../../../shared/components/inputs/select/select.component';
import {SortEvent} from 'primeng/api';
import {DatePicker} from 'primeng/datepicker';
import {Ripple} from 'primeng/ripple';
import {InputNumberModule} from 'primeng/inputnumber';
import {Chip} from 'primeng/chip';
import {TableExpandControlsComponent} from '../../../shared/components/table/table-expand-controls/table-expand-controls.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {NoDataComponent} from '../../../shared/components/no-data/no-data.component';
import {GlobalSearchComponent} from '../../../shared/components/search/global-search/global-search.component';
import {SelectTranslationOption} from '../../../shared/components/inputs/select/select.types';
import {OptionsSelectComponent} from '../../../shared/components/inputs/options-select/options-select.component';
import {
  SelectOption,
  SelectValue,
} from '../../../shared/components/inputs/options-select/options-select.types';
import {ContractDetailsDialogComponent} from './contract-details-dialog/contract-details-dialog.component';
import {ContractDetailsApplyEvent} from './contract-details-dialog/contract-details-dialog.types';

@Component({
  selector: 'app-selecthor-admin-provider-list',
  templateUrl: './selecthor-admin-provider-list.component.html',
  imports: [
    FormsModule,
    TableModule,
    TranslocoPipe,
    BackgroundCardComponent,
    ConfirmStepLeaveDirective,
    Button,
    SelectComponent,
    DatePicker,
    Ripple,
    InputNumberModule,
    Chip,
    TableExpandControlsComponent,
    LoadingComponent,
    NoDataComponent,
    GlobalSearchComponent,
    OptionsSelectComponent,
    ContractDetailsDialogComponent,
  ],
})
export class SelecthorAdminProviderListComponent {
  private readonly adminProviderContractController = inject(AdminProviderContractControllerService);
  private readonly toastService = inject(ToastService);
  private readonly directusService = inject(DirectusService);

  readonly saving = signal(false);
  readonly providers = signal<ProviderContractRow[]>([]);
  readonly originalProviders = signal<ProviderContractRow[]>([]);
  readonly hasUnsavedChanges = signal(false);

  readonly expandedRows = signal<Record<string, boolean>>({});
  readonly selectedProvider = signal<ProviderContractRow | null>(null);

  readonly providerPaymentPlans = toSignal(this.directusService.loadProviderPaymentPlans(), {
    initialValue: [],
  });

  private readonly providersResource = rxResource({
    params: () => {
      const plans = this.providerPaymentPlans();
      return plans.length ? plans : undefined;
    },
    stream: () =>
      forkJoin({
        apiData: this.adminProviderContractController.loadAllProvidersWithContracts(),
        hrTools: this.directusService.loadHrTools(),
      }).pipe(
        map(({apiData, hrTools}) => this.transformApiDataToProviderTableData(apiData, hrTools)),
      ),
  });

  readonly loading = computed(
    () => this.providersResource.isLoading() && this.providers().length === 0,
  );

  readonly sortField = signal('companyName');
  readonly sortOrder = signal(1);

  readonly globalFilterFields = ['companyName', 'adminFirstName', 'adminLastName', 'adminEmail'];

  readonly providerContractStatusOptions: SelectOption<ProviderContractStatus>[] = Object.values(
    ProviderContractStatus,
  ).map((status) => ({directusKey: 'ProviderContractStatus.' + status, value: status}));

  readonly hasProviders = computed(() => this.providers().length > 0);
  readonly contractDetailsVisible = computed(() => this.selectedProvider() !== null);
  readonly changedDatesValid = computed(() =>
    this.getChangedProviders().every(
      (p) =>
        !p.contractStart || !p.contractEnd || p.contractEnd.getTime() > p.contractStart.getTime(),
    ),
  );
  readonly shouldShowSaveButton = computed(
    () => this.hasUnsavedChanges() && !this.saving() && this.changedDatesValid(),
  );

  constructor() {
    effect(() => this.applyLoadedProviders());
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field ?? 'companyName');
    this.sortOrder.set(event.order ?? 1);
  }

  onPlanChange(provider: ProviderContractRow, planIdToStore: SelectTranslationOption | null): void {
    this.providers.set(
      this.providers().map((p) =>
        p.tenantId === provider.tenantId ? {...p, selectedPlan: planIdToStore?.id ?? null} : p,
      ),
    );

    this.hasUnsavedChanges.set(true);
  }

  onDateChange(
    provider: ProviderContractRow,
    field: 'contractStart' | 'contractEnd',
    date: Date | null,
  ): void {
    this.providers.set(
      this.providers().map((p) => (p.tenantId === provider.tenantId ? {...p, [field]: date} : p)),
    );
    this.hasUnsavedChanges.set(true);
  }

  openContractDetails(provider: ProviderContractRow): void {
    this.selectedProvider.set(provider);
  }

  closeContractDetails(): void {
    this.selectedProvider.set(null);
  }

  onContractDetailsApplied(details: ContractDetailsApplyEvent): void {
    const selectedProvider = this.selectedProvider();
    if (!selectedProvider) return;

    const updatedProviders: ProviderContractRow[] = this.providers().map((row) => {
      if (row.tenantId !== selectedProvider.tenantId) {
        return row;
      }
      return {...row, ...details};
    });

    this.providers.set(updatedProviders);
    this.hasUnsavedChanges.set(true);
    this.closeContractDetails();
  }

  async saveAllChanges(): Promise<void> {
    if (!this.hasUnsavedChanges()) return;

    const changedProviders = this.getChangedProviders();

    if (changedProviders.length === 0) {
      this.hasUnsavedChanges.set(false);
      return;
    }

    this.saving.set(true);

    try {
      for (const provider of changedProviders) {
        const planDirectusId = provider.selectedPlan ?? '';

        const request: ProviderContractRequest = {
          tenantId: provider.tenantId,
          planDirectusId: planDirectusId,
          status: provider.status,
          contractEndDate: provider.contractEnd?.toISOString(),
          contractStartDate: provider.contractStart?.toISOString(),
          commissionRate:
            provider.commissionRate !== undefined ? provider.commissionRate / 100 : undefined,
          partnerFeeAmount: provider.partnerFeeAmount,
          initialContractDurationYears: provider.initialContractDurationYears,
          directusProviderFeeFirstDueDateUuid: provider.directusProviderFeeFirstDueDateUuid,
          directusProviderFeeRecurringDueDateUuid: provider.directusProviderFeeRecurringDueDateUuid,
          leadRegistrationUrl: provider.leadRegistrationUrl,
        };

        const requestParams: UpsertProviderContractRequestParams = {
          tenantId: provider.tenantId,
          providerContractRequest: request,
        };

        await firstValueFrom(
          this.adminProviderContractController.upsertProviderContract(requestParams),
        );
      }

      await this.toastService.showSuccess(
        'selecthorAdmin.providerList.changesSaved',
        `${changedProviders.length} provider contract(s) updated successfully`,
      );

      this.hasUnsavedChanges.set(false);
      this.providersResource.reload();
    } finally {
      this.saving.set(false);
    }
  }

  expandAll(): void {
    this.expandedRows.set(
      this.providers().reduce<Record<string, boolean>>((acc, p) => {
        acc[p.tenantId] = true;
        return acc;
      }, {}),
    );
  }

  collapseAll(): void {
    this.expandedRows.set({});
  }

  protected onStatusChange(
    provider: ProviderContractRow,
    status: SelectValue<ProviderContractStatus>,
  ): void {
    if (!status) return;
    this.providers.set(
      this.providers().map((p) => (p.tenantId === provider.tenantId ? {...p, status} : p)),
    );

    this.hasUnsavedChanges.set(true);
  }

  private applyLoadedProviders(): void {
    const data = this.providersResource.value();
    if (!data) return;

    this.providers.set(data);
    this.originalProviders.set(structuredClone(data));
    this.hasUnsavedChanges.set(false);

    if (data.length === 0) {
      void this.toastService.showWarning(
        'selecthorAdmin.providerList.noProviders',
        'selecthorAdmin.providerList.noProvidersDetail',
      );
    }
  }

  private hasProviderChanged(current: ProviderContractRow, original: ProviderContractRow): boolean {
    if (current.tenantId !== original.tenantId) return true;

    return (
      current.selectedPlan !== original.selectedPlan ||
      current.contractStart?.getTime() !== original.contractStart?.getTime() ||
      current.contractEnd?.getTime() !== original.contractEnd?.getTime() ||
      current.status !== original.status ||
      current.commissionRate !== original.commissionRate ||
      current.partnerFeeAmount !== original.partnerFeeAmount ||
      current.initialContractDurationYears !== original.initialContractDurationYears ||
      current.directusProviderFeeFirstDueDateUuid !==
        original.directusProviderFeeFirstDueDateUuid ||
      current.directusProviderFeeRecurringDueDateUuid !==
        original.directusProviderFeeRecurringDueDateUuid ||
      current.leadRegistrationUrl !== original.leadRegistrationUrl
    );
  }

  private getChangedProviders(): ProviderContractRow[] {
    const currentProviders = this.providers();
    const originalProviders = this.originalProviders();

    return currentProviders.filter((current) => {
      const original = originalProviders.find((orig) => orig.tenantId === current.tenantId);
      return original ? this.hasProviderChanged(current, original) : true;
    });
  }

  private transformApiDataToProviderTableData(
    apiData: ProviderContractResponse[],
    hrTools: HrTool[],
  ): ProviderContractRow[] {
    return apiData
      .filter((response) => response.tenantInfo)
      .map((response) => {
        const selectedPlan = response.plan && response.plan.trim() !== '' ? response.plan : null;

        const tools = hrTools.filter(
          (tool) => tool.toolProvider.id === response.hrToolProviderDirectusId,
        );
        return {
          hrToolProviderDirectusId: response.hrToolProviderDirectusId ?? '',
          tenantId: response.tenantId,
          companyName: `${response.hrToolProvider?.name}`.trim(),
          adminFirstName: response.tenantInfo.firstName ?? '',
          adminLastName: response.tenantInfo.lastName ?? '',
          adminEmail: response.tenantInfo.email ?? '',
          selectedPlan: selectedPlan,
          tools: tools,
          contractEnd: response.contractEnd ? new Date(response.contractEnd) : null,
          contractStart: response.contractStart ? new Date(response.contractStart) : null,
          status: response.status,
          commissionRate:
            response.commissionRate !== undefined ? response.commissionRate * 100 : undefined,
          partnerFeeAmount: response.partnerFeeAmount,
          initialContractDurationYears: response.initialContractDurationYears,
          directusProviderFeeFirstDueDateUuid: response.directusProviderFeeFirstDueDateUuid,
          directusProviderFeeRecurringDueDateUuid: response.directusProviderFeeRecurringDueDateUuid,
          leadRegistrationUrl: response.leadRegistrationUrl,
        };
      });
  }
}
